import { Skeleton } from '@material-ui/lab';
import React, { createContext, Dispatch, SetStateAction, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { VariableTab } from '../../components/RouteTabs';
import FixedTab from '../../components/RouteTabs/FixedTab';
import RouteTabs from '../../components/RouteTabs/RouteTabs';
import { useTransferContract } from '../../hooks/queries/useTransferContract';
import { useVariableTabState } from '../../hooks/useVariableTabState';
import { ITransferContract } from '../../services/types';
import { RouteNames } from '../config';
import CoinUnitPrice from './CoinUnitPrice/CoinUnitPrice';
import ContractView from './ContractView';
import ManageContracts from './ManageContracts';
import TransferList from './TransferList';
import MonthlyRoyaltyExpenseList from './MonthlyRoyaltyExpenseList';

interface IProps {}

interface IContractListContext {
  focusIdOnCreate?: number;
  setFocusIdOnCreate?: Dispatch<SetStateAction<number | undefined>>;
}

const ContractLabel = ({ contractId }: { contractId: number }): JSX.Element => {
  const { data, isLoading } = useTransferContract(contractId);

  if (isLoading) {
    return <Skeleton width={140} />;
  }

  const story = data?.story || data?.season?.story;

  return <div style={{ width: 140, textOverflow: 'ellipsis', overflow: 'hidden' }}>{story?.title || contractId}</div>;
};

export const ContractListContext = createContext<IContractListContext>({});

const Payment = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.Payment;
  const [editingIds, setEditingIds] = useVariableTabState(path);
  const [focusIdOnCreate, setFocusIdOnCreate] = useState<number>();
  const history = useHistory();

  const handleEditClick = ({ id }: ITransferContract) => {
    if (!editingIds.includes(id)) {
      setEditingIds(ps => [...ps, id]);
    }
  };

  const handleCreate = (contract: ITransferContract) => {
    setFocusIdOnCreate(contract.id);
    history.push(`${RouteNames.Payment}?order=newest&page=1`);
  };

  const handleUpdate = () => {
    history.push(RouteNames.Payment);
  };

  const handleRemove = (contract: ITransferContract) => {
    history.push(RouteNames.Payment);
    handleTabClose(contract.id);
  };

  const getTabLabel = (contractId: number) => <ContractLabel contractId={contractId} />;

  const getTabUri = (contractId: number) => `v/${contractId}`;

  const handleTabClose = (contractId: number) => {
    setEditingIds(editingIds.filter(id => id !== contractId));
  };

  const keyExtractor = (contractId: number) => `tab-floating-${contractId}`;

  const variableTabRenderer = (contractId: number) => <ContractView contractId={contractId} onCreate={handleUpdate} onRemove={handleRemove} />;

  return (
    <RouteTabs path={path}>
      <FixedTab label="Manage Contracts">
        <ContractListContext.Provider value={{ focusIdOnCreate, setFocusIdOnCreate }}>
          <ManageContracts path={path} onEditClick={handleEditClick} />
        </ContractListContext.Provider>
      </FixedTab>
      <FixedTab label="Create a Contract" uri="create">
        <ContractView onCreate={handleCreate} />
      </FixedTab>
      <FixedTab label="Manage Transfers" uri="transfer">
        <TransferList />
      </FixedTab>
      <FixedTab label="Coin Unit Price" uri="coin">
        <CoinUnitPrice path={path} />
      </FixedTab>
      <FixedTab label="Monthly Royalty Expense" uri="monthly-royalty">
        <MonthlyRoyaltyExpenseList />
      </FixedTab>
      <VariableTab<number>
        data={editingIds}
        label={getTabLabel}
        uri={getTabUri}
        onClose={handleTabClose}
        keyExtractor={keyExtractor}
        render={variableTabRenderer}
      />
    </RouteTabs>
  );
};

export default Payment;
